import React from 'react';
import { Spinner } from './Spinner';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  style?: React.CSSProperties;
  preventCloseOnOverlayClick?: boolean;
}

/**
 * Modal - A reusable modal wrapper that provides consistent overlay, animation, and loading behaviour
 * 
 * Handles:
 * - Modal visibility with fadeIn/slideUp entrance animations
 * - Loading state with centered spinner
 * - Overlay click-to-close behavior
 * - Custom styling and class names for per-modal customization
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={handleClose} isLoading={loading}>
 *   <div className="modal-header">Title</div>
 *   <div className="modal-body">Content</div>
 * </Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  isLoading = false,
  className = '',
  style = {},
  preventCloseOnOverlayClick = false,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!preventCloseOnOverlayClick && !isLoading) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className={`modal-content ${className}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={style}
      >
        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}>
            <Spinner size="lg" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
